import { requireRevokeBadgesPermissions } from '../../../../../backend/features/badges/shared';
import { hasPermissionsToAccessAccounts } from '../../../../../backend/features/badges/shared/utils/hasPermissionsToAccessAccounts';
import { AccountBadgeContext } from '../../../../../ui/features/badge-management/contexts/AccountBadgeContext';
import { useAccountBadgeBulkActions } from '../../../../../ui/features/badge-management/hooks/useAccountBadgeBulkActions';
import { useBadgingTranslation } from '../../../../../ui/hooks';
import { DataTable } from '@juvare/common-data-table-ui/DataTable';
import { getAccountsManageFilter } from '@juvare/contact-management/backend/features/permissions/shared';
import { useUserState } from '@juvare/contact-management/ui/hooks';
import AddIcon from '@mui/icons-material/Add';
import BlockIcon from '@mui/icons-material/Block';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import PublishedWithChangesIcon from '@mui/icons-material/PublishedWithChanges';
import SendIcon from '@mui/icons-material/Send';
import React from 'react';
import { useAccountBadgeTable } from '../../hooks/useAccountBadgeTable';
import { AccountBadgeSlideout } from '../AccountBadgeSlideout/AccountBadgeSlideout';
export const AccountBadgeTable = ({ badgeId, accountBadges, refetchBadgeDetails }) => {
    var _a, _b, _c, _d;
    const { t } = useBadgingTranslation();
    const userState = useUserState();
    const accountFilters = getAccountsManageFilter(userState);
    const { dataTableProps, reload, clearActiveRow } = useAccountBadgeTable({
        initialFilter: [
            {
                op: '=',
                column: 'badgeId',
                value: badgeId,
            },
            {
                column: 'accountId',
                op: 'in',
                subFilter: {
                    entity: 'account',
                    filter: accountFilters,
                },
            },
        ],
    });
    const appliedStorageFilters = (_c = (_b = (_a = dataTableProps.customBackendResponseData) === null || _a === void 0 ? void 0 : _a.storageClientRequest) === null || _b === void 0 ? void 0 : _b.filter) !== null && _c !== void 0 ? _c : [];
    const reloadAccountBadgeData = React.useCallback(() => {
        void refetchBadgeDetails();
        reload();
    }, [refetchBadgeDetails, reload]);
    const bulkActions = useAccountBadgeBulkActions({
        badgeId,
        accountBadgesAll: accountBadges,
        accountBadgeRows: dataTableProps.rows,
        accountBadgeSelectedIds: (_d = dataTableProps.selectedRowIds) !== null && _d !== void 0 ? _d : [],
        onActionCompleted: reloadAccountBadgeData,
        appliedStorageFilters,
        filteredRowCount: dataTableProps.filteredRowCount,
    });
    const { buildAccountBadgeSlideoutComponent } = React.useContext(AccountBadgeContext);
    const AccountBadgeSlideoutImpl = React.useMemo(() => { var _a; return (_a = buildAccountBadgeSlideoutComponent === null || buildAccountBadgeSlideoutComponent === void 0 ? void 0 : buildAccountBadgeSlideoutComponent(AccountBadgeSlideout)) !== null && _a !== void 0 ? _a : null; }, [buildAccountBadgeSlideoutComponent]);
    const canManageAccounts = hasPermissionsToAccessAccounts(userState).canManageSomeAccountsViaAnyMeans;
    return (React.createElement(React.Fragment, null,
        React.createElement(DataTable, Object.assign({}, dataTableProps, { globalActions: canManageAccounts
                ? [
                    {
                        icon: React.createElement(AddIcon, null),
                        label: t('badges.badgeDetails.accountBadgeTable.actions.addAccount'),
                        disabled: !bulkActions.canAddAccount,
                        onClick: bulkActions.handleAddAccount,
                    },
                ]
                : [], localActions: [
                {
                    icon: React.createElement(SendIcon, null),
                    label: t('badges.badgeDetails.accountBadgeTable.actions.notify'),
                    onClick: bulkActions.handleNotify,
                },
                {
                    icon: React.createElement(PrintOutlinedIcon, null),
                    label: t('badges.badgeDetails.accountBadgeTable.actions.print'),
                    onClick: bulkActions.handlePrintAccountBadges,
                },
                {
                    icon: React.createElement(BlockIcon, null),
                    label: t('badges.badgeDetails.accountBadgeTable.actions.revoke'),
                    disabled: !requireRevokeBadgesPermissions(userState),
                    onClick: bulkActions.handleRevoke,
                },
                {
                    icon: React.createElement(PublishedWithChangesIcon, null),
                    label: t('badges.badgeDetails.accountBadgeTable.actions.changeDistribution'),
                    onClick: bulkActions.handleChangeDistributionStatus,
                },
                {
                    icon: React.createElement(PrintOutlinedIcon, null),
                    label: t('badges.badgeDetails.accountBadgeTable.actions.reissue'),
                    onClick: bulkActions.handleReissueAccountBadgesBulkActionDialog,
                },
            ] })),
        AccountBadgeSlideoutImpl && dataTableProps.activeRowId && (React.createElement(AccountBadgeSlideoutImpl, { accountBadgeId: dataTableProps.activeRowId, refetchAccountBadgeList: reloadAccountBadgeData, onClose: (changed) => {
                clearActiveRow();
                if (changed) {
                    reloadAccountBadgeData();
                }
            } }))));
};
